import { Classes, Icon, IconName } from '@clarion-blueprint-shim';
import { ReactNode } from 'react';
import {
  TextField as RACTextField,
  TextFieldProps as RACTextFieldProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import {
  ClarionDescription,
  ClarionFieldError,
  ClarionFieldGroup,
  ClarionInput,
  ClarionLabel,
} from './ClarionField';

export interface ClarionTextFieldProps extends Omit<RACTextFieldProps, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  placeholder?: string;

  // Blueprint InputGroup compatibility props
  leftIcon?: IconName;
  rightElement?: ReactNode;
  leftElement?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  round?: boolean;
  fill?: boolean;
  asyncControl?: boolean;
  inputSize?: number;

  // Additional props for flexibility
  children?: ReactNode;
}

const textFieldStyles = tv({
  base: 'flex flex-col gap-1',
  variants: {
    fill: {
      true: 'w-full',
    },
  },
});

const textFieldInputStyles = tv({
  base: '',
  variants: {
    isInvalid: {
      true: Classes.INTENT_DANGER,
    },
    round: {
      true: Classes.ROUND,
    },
    size: {
      small: Classes.SMALL,
      medium: '',
      large: Classes.LARGE,
    },
  },
});

export function ClarionTextField({
  label,
  description,
  errorMessage,
  placeholder,
  leftIcon,
  rightElement,
  size = 'medium',
  round,
  fill,
  asyncControl,
  inputSize,
  children,
  className,
  ...props
}: ClarionTextFieldProps) {
  return (
    <RACTextField
      {...props}
      className={composeRenderProps(className, (className, renderProps) =>
        textFieldStyles({ ...renderProps, fill, className }),
      )}
    >
      {label && <ClarionLabel>{label}</ClarionLabel>}
      <ClarionFieldGroup
        className={cx(
          Classes.INPUT_GROUP,
          size === 'large' ? Classes.LARGE : size === 'small' ? Classes.SMALL : '',
        )}
      >
        {leftIcon && <Icon icon={leftIcon} className={Classes.ICON} />}
        <ClarionInput
          placeholder={placeholder}
          size={inputSize}
          className={textFieldInputStyles({ size, isInvalid: props.isInvalid, round })}
        />
        {rightElement}
      </ClarionFieldGroup>
      {description && <ClarionDescription>{description}</ClarionDescription>}
      <ClarionFieldError>{errorMessage}</ClarionFieldError>
      {children}
    </RACTextField>
  );
}
